<template>
    <div class="step-wrapper">
        <div class="step-header">
            <h2 class="step-title">Create your account</h2>
            <p class="step-subtitle">Get personalized housing recommendations.</p>
        </div>

        <div class="step-body">
            <!-- Social Sign-in Buttons at Top -->
            <div class="social-buttons">
                <v-btn
                    outlined
                    block
                    height="50"
                    class="social-btn google-btn"
                    @click="signInWithGoogle"
                >
                    <img src="../../../assets/icons/icon-google.svg" alt="Google" class="social-icon" />
                    Continue with Google
                </v-btn>

                <!-- Apple sign-in hidden until Apple keys are configured
                <v-btn
                    outlined
                    block
                    height="50"
                    class="social-btn apple-btn"
                    @click="signInWithApple"
                >
                    <v-icon class="social-icon" size="20">mdi-apple</v-icon>
                    Continue with Apple
                </v-btn>
                -->
            </div>

            <div class="divider-section">
                <v-divider />
                <span class="divider-text">or sign up with email</span>
                <v-divider />
            </div>

            <v-form ref="form" v-model="valid" lazy-validation>
                <v-text-field
                    v-model="firstName"
                    label="First Name"
                    :rules="nameRules"
                    outlined
                    required
                    class="mb-2"
                />

                <v-text-field
                    v-model="lastName"
                    label="Last Name"
                    :rules="nameRules"
                    outlined
                    required
                    class="mb-2"
                />

                <v-text-field
                    v-model="email"
                    label="Email"
                    :rules="emailRules"
                    outlined
                    required
                    class="mb-2"
                />

                <v-text-field
                    v-model="phone"
                    label="Phone Number"
                    :rules="phoneRules"
                    outlined
                    required
                    v-mask="'(###) ###-####'"
                    class="mb-2"
                />

                <v-text-field
                    v-model="password"
                    label="Password"
                    :rules="passwordRules"
                    :type="showPassword ? 'text' : 'password'"
                    :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="showPassword = !showPassword"
                    outlined
                    required
                />
            </v-form>
        </div>

        <div class="step-footer">
            <v-btn
                color="primary"
                depressed
                block
                height="50"
                :disabled="!valid"
                :loading="loading"
                @click="onSubmit"
                class="next-btn"
            >
                Create Account
            </v-btn>

            <p class="terms-text">
                By creating an account, you agree to our
                <router-link to="/terms">Terms of Service</router-link>
                and
                <router-link to="/policy">Privacy Policy</router-link>.
            </p>
        </div>
    </div>
</template>

<script>
import { GOOGLE_CLIENT_ID } from '@/constants';

export default {
    name: 'RegistrationStep',

    data() {
        return {
            valid: false,
            loading: false,
            showPassword: false,
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            password: '',
            auth2: null,
            nameRules: [
                v => !!v || 'This field is required'
            ],
            emailRules: [
                v => !!v || 'Email is required',
                v => /.+@.+\..+/.test(v) || 'Email must be valid'
            ],
            phoneRules: [
                v => !!v || 'Phone number is required',
                v => (v && v.length >= 14) || 'Phone number must be complete'
            ],
            passwordRules: [
                v => !!v || 'Password is required',
                v => (v && v.length >= 8) || 'Password must be at least 8 characters'
            ]
        };
    },

    computed: {
        answers() {
            return this.$store.getters['Questionnaire/getAllAnswers'];
        }
    },

    methods: {
        buildUserData() {
            const answers = this.answers;
            return {
                name: `${this.firstName} ${this.lastName}`.trim(),
                email: this.email,
                phone_number: this.phone,
                password: this.password,
                type: 'student',
                allow_subscription: false,
                needs_roommate: false,
                university_start_date: this.formatDateForApi(answers.moveInDate),
                // Map questionnaire answers to user fields
                school_id: answers.school?.id || null,
                campus_id: answers.school?.campus_id || null,
                preferred_pet_friendly: answers.pets !== 'none',
                has_pets: answers.pets !== 'none',
                has_eviction_history: answers.hasEviction || false,
                has_car: answers.transportMode === 'drive',
                preferred_move_in_date: answers.moveInDate || null,
                program_length: answers.programLength || null,
                preferred_housing_type: answers.housingType || null,
                preferred_transport_mode: answers.transportMode || null,
                cosigner_type: answers.cosignerType || null
            };
        },

        formatDateForApi(date) {
            if (!date) return '';
            const [year, month, day] = date.split('-');
            return `${month}/${day}/${year}`;
        },

        async onSubmit() {
            if (!this.$refs.form.validate()) return;

            this.loading = true;
            try {
                const userData = this.buildUserData();
                await this.$store.dispatch('register', userData);

                // Clear questionnaire state
                this.$store.dispatch('Questionnaire/resetQuestionnaire');

                // Router redirect is handled by the register action
            } catch (error) {
                this.$store.commit('setSnackbar', {
                    show: true,
                    message: error.message || 'Registration failed. Please try again.',
                    color: 'red'
                });
            } finally {
                this.loading = false;
            }
        },

        async signInWithGoogle() {
            try {
                this.loading = true;
                const googleUser = await this.auth2.signIn();
                const profile = googleUser.getBasicProfile();

                // Build registration data with questionnaire answers
                const answers = this.answers;
                const userData = {
                    name: profile.getName(),
                    email: profile.getEmail(),
                    type: 'student',
                    allow_subscription: false,
                    needs_roommate: false,
                    university_start_date: this.formatDateForApi(answers.moveInDate),
                    // Questionnaire data
                    school_id: answers.school?.id || null,
                    campus_id: answers.school?.campus_id || null,
                    preferred_pet_friendly: answers.pets !== 'none',
                    has_pets: answers.pets !== 'none',
                    has_eviction_history: answers.hasEviction || false,
                    has_car: answers.transportMode === 'drive',
                    preferred_move_in_date: answers.moveInDate || null,
                    program_length: answers.programLength || null,
                    preferred_housing_type: answers.housingType || null,
                    preferred_transport_mode: answers.transportMode || null,
                    cosigner_type: answers.cosignerType || null
                };

                await this.$store.dispatch('socialRegister', userData);

                // Clear questionnaire state after successful registration
                this.$store.dispatch('Questionnaire/resetQuestionnaire');
            } catch (error) {
                if (error.error !== 'popup_closed_by_user') {
                    // Handle "account already exists" error from backend
                    const message = error.response?.data?.message ||
                        'Google sign-in failed. Please try again.';
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: message,
                        color: 'red'
                    });
                }
            } finally {
                this.loading = false;
            }
        },

        signInWithApple() {
            // Apple sign-in not yet implemented
            this.$store.commit('setSnackbar', {
                show: true,
                message: 'Apple sign-in coming soon. Please use Google or email registration.',
                color: 'info'
            });
        },

        initGoogleAuth() {
            if (window.gapi) {
                window.gapi.load('auth2', () => {
                    this.auth2 = window.gapi.auth2.init({
                        client_id: GOOGLE_CLIENT_ID
                    });
                });
            }
        }
    },

    mounted() {
        this.initGoogleAuth();
    }
};
</script>

<style scoped>
.step-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.step-header {
    text-align: center;
    margin-bottom: 24px;
}

.step-title {
    font-size: 24px;
    font-weight: 700;
    color: #333;
    margin-bottom: 8px;
}

.step-subtitle {
    font-size: 16px;
    color: #666;
}

.step-body {
    flex: 1;
}

.social-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 8px;
}

.social-btn {
    text-transform: none;
    font-weight: 500;
    border-color: #ddd;
    justify-content: center;
}

.social-btn:hover {
    border-color: #bbb;
}

.google-btn {
    background: white;
}

.apple-btn {
    background: #000;
    color: white !important;
    border-color: #000;
}

.apple-btn:hover {
    background: #333;
    border-color: #333;
}

.social-icon {
    width: 20px;
    height: 20px;
    margin-right: 10px;
}

.apple-btn .social-icon {
    color: white;
}

.divider-section {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 20px 0;
}

.divider-text {
    color: #999;
    font-size: 13px;
    white-space: nowrap;
}

.step-footer {
    margin-top: auto;
    padding-top: 24px;
}

.next-btn {
    font-weight: 600;
    text-transform: none;
    font-size: 16px;
    border-radius: 8px;
}

.terms-text {
    text-align: center;
    font-size: 12px;
    color: #666;
    margin-top: 16px;
}

.terms-text a {
    color: #2D76E3;
    text-decoration: none;
}

.terms-text a:hover {
    text-decoration: underline;
}
</style>
